import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  createCouponCode,
  updateCouponCode,
  fetchCouponCodebyId,
} from "../../../services/couponService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { InputField } from "../../Common/FormElements/InputField";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { fetchProductDropdowns } from "../../../services/productService";
import { SelectField } from "../../Common/FormElements/SelectField";
import { fetchCustomers } from "../../../services/customerService";

export const AddCoupon = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedCouponCode,
  setEditMode,
}) => {
  const couponType = [
    { id: "Value", couponTypeName: "Value" },
    { id: "Percentage", couponTypeName: "Percentage" },
  ];

  // Format dates for the stored procedure (YYYY-MM-DD format)
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    // Use local date to avoid timezone issues
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isCouponValueType, setIsCouponValueType] = useState(false);

  const navigate = useNavigate();
  const PageLevelAccessurl = "coupon";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [dropdownProducts, setDropdownProducts] = useState([]);
  const [dropdownCustomers, setDropdownCustomers] = useState([]);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const productIdRef = useRef(null);
  const couponCodeRef = useRef(null);
  const couponTypeRef = useRef(null);
  const couponValueRef = useRef(null);
  const minValueRef = useRef(null);
  const noOfUsagesRef = useRef(null);
  const expiryDateRef = useRef(null);
  const customerIdRef = useRef(null);
  const couponDescriptionRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    ProductId: 0,
    CouponCode: "",
    CouponType: "",
    CouponValue: 0,
    MinValue: 0,
    NoOfUsages: 1,
    ExpiryDate: "",
    CustomerId: "",
    Published: false,
    CouponDescription: "",
  });

  // ✅ Register all fields
  useEffect(() => {
    registerField("ProductId", productIdRef, "Product Id");
    registerField("CouponCode", couponCodeRef, "Coupon Code");
    registerField("CouponType", couponTypeRef, "Coupon Type");
    registerField("CouponValue", couponValueRef, "Coupon Value");
    registerField("MinValue", minValueRef, "Min Value");
    registerField("NoOfUsages", noOfUsagesRef, "No Of Usages");
    registerField("ExpiryDate", expiryDateRef, "Expiry Date");
    registerField("CustomerId", customerIdRef, "Customer Id");
    registerField(
      "CouponDescription",
      couponDescriptionRef,
      "Coupon Description"
    );
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);
  useEffect(() => {
    const dateInput = document.getElementById("ExpiryDate");

    if (dateInput) {
      const fp = flatpickr(dateInput, {
        dateFormat: "d-M-Y",
        monthSelectorType: "static",
        onChange: (selectedDates, dateStr) => {
          setFormValues((prevData) => ({
            ...prevData,
            ExpiryDate: formatDate(dateStr),
          }));
        },
      });

      // Cleanup function to destroy the flatpickr instance
      return () => {
        if (fp) {
          fp.destroy();
        }
      };
    }
  }, []); // Add empty dependency array to run only once

  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const loadProducts = async () => {
    try {
      const productsData = await fetchProductDropdowns();
      if (
        productsData &&
        productsData.result &&
        productsData.result.length > 0
      ) {
        setDropdownProducts(productsData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  const loadCustomers = async () => {
    try {
      const customersData = await fetchCustomers();
      if (
        customersData &&
        customersData.result &&
        customersData.result.length > 0
      ) {
        setDropdownCustomers(customersData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      if (editMode && initialData.id) {
        try {
          const data = await fetchCouponCodebyId(initialData.id);
          loadProducts();
          loadCustomers();
          if (data && data.isSuccess && data.result) {
            setFormValues({
              ProductId: data.result.productId,
              CouponCode: data.result.couponCode,
              CouponType: data.result.couponType,
              CouponValue: data.result.couponValue,
              MinValue: data.result.minValue,
              NoOfUsages: data.result.noOfUsages,
              ExpiryDate: formatDate(data.result.expiryDate),
              CustomerId: data.result.customerId,
              Published: data.result.published,
              CouponDescription: data.result.couponDescription || "",
            });

            if (data.result.couponType === "Value") {
              setIsCouponValueType(true);
            } else {
              setIsCouponValueType(false);
            }
          }
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormValues({
          ProductId: 0,
          CouponCode: "",
          CouponType: "",
          CouponValue: 0,
          MinValue: 0,
          NoOfUsages: 1,
          ExpiryDate: "",
          CustomerId: "",
          Published: false,
          CouponDescription: "",
        });
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    let inputValue = type === "checkbox" ? checked : value;
    if (id === "ExpiryDate") {
      inputValue = formatDate(inputValue);
    }
    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };
      if (id === "CouponType") {
        setIsCouponValueType(inputValue === "Value");
      }
      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      productIdRef,
      couponCodeRef,
      couponTypeRef,
      couponValueRef,
      minValueRef,
      noOfUsagesRef,
      expiryDateRef,
      customerIdRef,
      couponDescriptionRef,
    };
    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ✅ Touch all fields to show validation errors
      touchAllFields();

      // ✅ Validate all fields using refs
      const isValid = validateAllFields();

      if (!isValid) {
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      try {
        if (editMode) {
          setIsButtonDisabled(true);
          var updateResponseData = await updateCouponCode({
            ...formValues,
            Id: initialData.id,
          });
          // ✅ Clear form errors
          clearErrors();
          if (updateResponseData?.data.isSuccess) {
            toast.success(updateResponseData?.data.statusMessage);
          } else {
            toast.error(updateResponseData?.data.statusMessage);
          }
        } else {
          setIsButtonDisabled(true);
          var createResponseData = await createCouponCode(formValues);
          resetAllFields();
          setFormValues({
            ProductId: 0,
            CouponCode: "",
            CouponType: "",
            CouponValue: 0,
            MinValue: 0,
            NoOfUsages: 1,
            ExpiryDate: "",
            CustomerId: "",
            Published: false,
            CouponDescription: "",
          });
          // ✅ Clear form errors
          clearErrors();
          if (createResponseData?.data.isSuccess) {
            toast.success(createResponseData?.data.statusMessage);
          } else {
            toast.error(createResponseData?.data.statusMessage);
          }
        }
        if (onSuccess) onSuccess();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formValues, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    setFormValues({
      ProductId: 0,
      CouponCode: "",
      CouponType: "",
      CouponValue: 0,
      MinValue: 0,
      NoOfUsages: 1,
      ExpiryDate: "",
      CustomerId: "",
      Published: false,
    });
    resetAllFields();
    setSelectedCouponCode(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="HSN Code" />
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Coupon" : "Add Coupon"}
              </h5>
              <div className="d-flex align-items-center">
                <label htmlFor="Published" className="form-label pe-3">
                  Published Coupon
                </label>
                <div className="form-check form-switch form-switch-custom form-switch-primary">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="Published"
                    name="Published"
                    checked={formValues.Published}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="Published">
                    {formValues.Published ? "Yes" : "No"}
                  </label>
                </div>
              </div>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <SelectField
                      label="Coupon Type"
                      ref={couponTypeRef}
                      id="CouponType"
                      value={formValues.CouponType}
                      onChange={handleInputChange}
                      options={couponType}
                      optionValueKey="id"
                      optionLabelKey="couponTypeName"
                      placeholder="Select Coupon Type"
                      required={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={couponCodeRef}
                      label="Coupon Code"
                      id="CouponCode"
                      value={formValues.CouponCode}
                      placeholder="Enter your Coupon Code"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={15}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={couponValueRef}
                      label="Coupon Value"
                      id="CouponValue"
                      value={formValues.CouponValue}
                      placeholder="Enter your Coupon Value"
                      onChange={handleInputChange}
                      required={true}
                      validationType={
                        isCouponValueType ? "numberonly" : "percentage"
                      }
                      maxLength={6}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={minValueRef}
                      label="Minimum Cart Value"
                      id="MinValue"
                      value={formValues.MinValue}
                      placeholder="Enter your Minimum Cart Value"
                      onChange={handleInputChange}
                      required={true}
                      validationType={"decimal"}
                      maxLength={10}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={noOfUsagesRef}
                      label="No Of Usages"
                      id="NoOfUsages"
                      value={formValues.NoOfUsages}
                      placeholder="Enter your No Of Usage Allowed"
                      onChange={handleInputChange}
                      required={true}
                      validationType={"numberonly"}
                      maxLength={10}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={expiryDateRef}
                      label="Expiry Date"
                      id="ExpiryDate"
                      value={formValues.ExpiryDate}
                      placeholder="Enter Expiry Date"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={20}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12 d-none">
                    <SelectField
                      label="Product"
                      ref={productIdRef}
                      id="ProductId"
                      value={formValues.ProductId}
                      onChange={handleInputChange}
                      options={dropdownProducts}
                      optionValueKey="id"
                      optionLabelKey="productName"
                      placeholder="Select Product"
                      required={false}
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-12 d-none">
                    <SelectField
                      label="Customer"
                      ref={customerIdRef}
                      id="CustomerId"
                      value={formValues.CustomerId}
                      onChange={handleInputChange}
                      options={dropdownCustomers}
                      optionValueKey="id"
                      optionLabelKey="customerId"
                      placeholder="Select Customer"
                      required={false}
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={couponDescriptionRef}
                      label="Coupon Description"
                      id="CouponDescription"
                      value={formValues.CouponDescription}
                      placeholder="Enter your Coupon Description"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled || !isFormValid}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
